// components/received/AuthenticationModal.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';

const AuthenticationModal = ({ show, onSuccess, onError, endpoints }) => {
  // Auth states
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Forgot credentials states
  const [showForgotForm, setShowForgotForm] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showNewCredentials, setShowNewCredentials] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(endpoints.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.error) {
        onError(data.error);
      } else {
        // Store the login password for potential password change
        sessionStorage.setItem('loginPassword', password);
        
        // Pass the data along with the login password
        onSuccess({
          ...data,
          loginPassword: password
        });
      }
    } catch (error) {
      onError(`Authentication failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle to forgot credentials form
  const toggleForgotCredentials = (e) => {
    e.preventDefault();
    setShowForgotForm(!showForgotForm);
    // Reset states
    setRecoveryEmail('');
    setOtp('');
    setShowOtpInput(false);
    setShowNewCredentials(false);
    setNewPassword('');
    setConfirmPassword('');
    setRecoveryMessage('');
  };

  // Handle OTP verification
  const handleVerifyEmail = async () => {
    if (!showOtpInput) {
      // STEP 1: Send OTP to email
      setIsLoading(true);

      try {
        const response = await fetch(endpoints.INITIATE_RECOVERY, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: recoveryEmail }),
        });

        const data = await response.json();

        if (data.success) {
          setShowOtpInput(true);
          // Show message within the modal instead of calling onSuccess()
          setRecoveryMessage('OTP sent to your email address. Please check your inbox.');
        } else {
          onError(data.message || 'Failed to send OTP');
        }
      } catch (error) {
        onError(`Error: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    } else {
      // STEP 2: Verify OTP
      setIsLoading(true);

      try {
        const response = await fetch(endpoints.VERIFY_RECOVERY, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: recoveryEmail,
            otp
          }),
        });

        const data = await response.json();

        if (data.success) {
          setShowNewCredentials(true);
          setShowOtpInput(false);
          setRecoveryMessage('OTP verified successfully. Please set your new password.');
        } else {
          onError(data.message || 'Invalid OTP');
        }
      } catch (error) {
        onError(`Error: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle save new credentials
  const handleSaveCredentials = async () => {
    if (newPassword !== confirmPassword) {
      onError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      onError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(endpoints.SAVE_NEW_CREDENTIALS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: recoveryEmail,
          newPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Reset form and go back to login
        setShowForgotForm(false);
        setShowNewCredentials(false);
        setRecoveryEmail('');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setRecoveryMessage('Password updated successfully! Please login with your new credentials.');

        // Clear login form
        setUsername('');
        setPassword('');
      } else {
        onError(data.message || 'Failed to update credentials');
      }
    } catch (error) {
      onError(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      centered
      className="re-auth-modal"
    >
      <Modal.Header>
        <Modal.Title>
          {showForgotForm
            ? showNewCredentials
              ? 'Set New Credentials'
              : 'Recover Your Credentials'
            : 'Authenticate to Access Endowment'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {recoveryMessage && (
          <div className="alert alert-info mb-3">
            {recoveryMessage}
          </div>
        )}
        {!showForgotForm ? (
          // Login Form
          <Form onSubmit={handleLogin}>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={isLoading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </Form.Group>

            <Button
              type="submit"
              className="re-btn-primary w-100 mb-3"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Authenticating...
                </>
              ) : (
                'Authenticate'
              )}
            </Button>

            <div className="text-center">
              <a
                href="#"
                onClick={toggleForgotCredentials}
                className="re-link"
              >
                Forgot credentials?
              </a>
            </div>
          </Form>
        ) : showNewCredentials ? (
          // New Credentials Form
          <Form>
            <div className="alert alert-warning mb-3">
              Please securely store your username and password. Never share them with anyone.
            </div>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </Form.Group>

            <Button
              type="button"
              className="re-btn-primary w-100 mb-3"
              onClick={handleSaveCredentials}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                'Save Credentials'
              )}
            </Button>

            <div className="text-center">
              <a
                href="#"
                onClick={toggleForgotCredentials}
                className="re-link"
              >
                Back to Login
              </a>
            </div>
          </Form>
        ) : (
          // Email Verification Form
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Enter your email</Form.Label>
              <Form.Control
                type="email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                required
                disabled={isLoading || showOtpInput}
              />
            </Form.Group>

            {showOtpInput && (
              <Form.Group className="mb-3">
                <Form.Label>Enter OTP</Form.Label>
                <Form.Control
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </Form.Group>
            )}

            <Button
              type="button"
              className="re-btn-primary w-100 mb-3"
              onClick={handleVerifyEmail}
              disabled={isLoading || !recoveryEmail || (showOtpInput && !otp)}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {showOtpInput ? 'Verifying...' : 'Sending...'}
                </>
              ) : (
                showOtpInput ? 'Verify OTP' : 'Send OTP'
              )}
            </Button>

            <div className="text-center">
              <a
                href="#"
                onClick={toggleForgotCredentials}
                className="re-link"
              >
                Back to Login
              </a>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

AuthenticationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  endpoints: PropTypes.object.isRequired
};

export default AuthenticationModal;